import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { CustomizationSectionRail } from './CustomizationSectionRail';
import { CustomizationGuidedPanel } from './CustomizationGuidedPanel';
import { CustomizationFooterBar } from './CustomizationFooterBar';
import { useCustomizationFlow } from './useCustomizationFlow';

const missingSummary = (missingList = []) => {
  return missingList.map(({ section, option }) => `${section?.name || 'Section'} - ${option?.name || 'Field'}`).join(', ');
};

export const CustomizationFlowModal = ({
  open,
  onOpenChange,
  title,
  description,
  loading,
  categories,
  values,
  setValues,
  isOptionVisible,
  formatCurrency,
  requireMandatory = true,
  onSubmit,
  submitLabel = 'Save',
  basePriceValue,
  basePriceDefault,
  onBasePriceChange,
  quantity,
  onQuantityChange,
  showQuantity = false,
  showDelete = false,
  onDeleteField,
  showAddCustomField = false,
  onAddCustomField,
  onCloseCleanup,
  isSubmitting = false,
  dataTestIdPrefix = 'customization-flow',
  referencePanel,
}) => {
  useEffect(() => {
    if (!open) {
      return;
    }
  }, [open]);

  const flow = useCustomizationFlow({
    categories: categories || [],
    values: values || {},
    isOptionVisible,
    requireMandatory,
  });

  const sectionProgressText = useMemo(() => {
    if (!flow.currentSection) return 'No section selected';
    const idx = flow.visibleSections.findIndex((section) => section.category_id === flow.currentSection.category_id);
    return `Section ${idx + 1} of ${flow.visibleSections.length}`;
  }, [flow.currentSection, flow.visibleSections]);

  const handleSubmit = () => {
    if (!flow.canSubmit) {
      const missing = flow.allMissingRequired;
      toast.error(`Please complete mandatory fields: ${missingSummary(missing)}`, { duration: 6000 });
      // Jump straight to the first section with a missing field instead of leaving
      // the user to hunt for it - this was previously a silent dead-end since the
      // submit button was HTML-disabled and never fired this handler at all.
      if (missing[0]?.section?.category_id) {
        flow.setCurrentSectionId(missing[0].section.category_id);
      }
      return;
    }
    onSubmit?.();
  };

  const handleCopyReferenceValue = async (item) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(item.value);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = item.value;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      toast.success(`Copied ${item.label}: ${item.value}`);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    const targetTag = String(event.target?.tagName || '').toLowerCase();
    if (targetTag === 'textarea') return;

    if (event.shiftKey) {
      event.preventDefault();
      flow.goPrevious();
      return;
    }

    if (flow.hasNext) {
      event.preventDefault();
      if (!flow.canGoNext) {
        toast.error('Complete required fields in this section before moving next.');
        return;
      }
      flow.goNext();
      return;
    }

    event.preventDefault();
    handleSubmit();
  };

  const handleClose = (nextOpen) => {
    onOpenChange?.(nextOpen);
    if (!nextOpen) {
      onCloseCleanup?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="w-[96vw] max-w-6xl !left-1/2 !top-4 !translate-x-[-50%] !translate-y-0 !flex !flex-col !gap-0 overflow-hidden !p-0"
        style={{ height: 'calc(100vh - 2rem)', maxHeight: 'calc(100vh - 2rem)' }}
        onKeyDown={handleKeyDown}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        data-testid={`${dataTestIdPrefix}-modal`}
      >
        <DialogHeader className="shrink-0 border-b border-slate-200 bg-white px-6 py-4">
          <DialogTitle className="text-2xl font-semibold text-slate-900" data-testid={`${dataTestIdPrefix}-title`}>
            {title}
          </DialogTitle>
          {!!description && (
            <DialogDescription data-testid={`${dataTestIdPrefix}-description`}>
              {description}
            </DialogDescription>
          )}
          <p className="text-xs text-slate-500" data-testid={`${dataTestIdPrefix}-progress`}>
            {sectionProgressText}
          </p>
        </DialogHeader>

        <div
          className="min-h-0 flex-1 overflow-hidden bg-white px-4 py-4 md:px-6"
          data-testid={`${dataTestIdPrefix}-body`}
        >
          <div className="h-full min-h-0 flex flex-col gap-4">
            <div className="shrink-0 grid gap-3 md:grid-cols-2">
              {onBasePriceChange && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3" data-testid={`${dataTestIdPrefix}-base-price-block`}>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-amber-700" data-testid={`${dataTestIdPrefix}-base-price-label`}>
                    Base Price
                  </Label>
                  <p className="mt-0.5 text-xs text-amber-700/80" data-testid={`${dataTestIdPrefix}-base-price-default`}>
                    Product default: {formatCurrency?.(basePriceDefault || 0)}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="font-medium text-amber-700">$</span>
                    <Input
                      type="number"
                      value={basePriceValue}
                      onChange={(e) => onBasePriceChange(parseFloat(e.target.value) || 0)}
                      className="h-10 text-right"
                      data-testid={`${dataTestIdPrefix}-base-price-input`}
                    />
                  </div>
                </div>
              )}

              {showQuantity && (
                <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={`${dataTestIdPrefix}-quantity-block`}>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600" data-testid={`${dataTestIdPrefix}-quantity-label`}>
                    Quantity
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    value={quantity || 1}
                    onChange={(e) => onQuantityChange?.(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="mt-2 h-10"
                    data-testid={`${dataTestIdPrefix}-quantity-input`}
                  />
                </div>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-hidden" data-testid={`${dataTestIdPrefix}-scroll-container`}>
              {loading ? (
                <div className="h-full overflow-y-auto space-y-3 pr-1" data-testid={`${dataTestIdPrefix}-loading`}>
                  {[...Array(6)].map((_, idx) => (
                    <Skeleton key={`flow-skeleton-${idx}`} className="h-20 w-full" />
                  ))}
                </div>
              ) : (categories || []).length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid={`${dataTestIdPrefix}-empty-state`}>
                  No customizations available for this product.
                </div>
              ) : (
                <div className="grid h-full min-h-0 gap-4 lg:grid-cols-[18rem_minmax(0,1fr)_15rem]">
                  <div className="min-h-0 h-full overflow-hidden rounded-2xl">
                    <ScrollArea
                      type="always"
                      className="min-h-0 h-full"
                      data-testid={`${dataTestIdPrefix}-rail-scroll`}
                    >
                      <CustomizationSectionRail
                        sections={flow.visibleSections}
                        activeSectionId={flow.currentSectionId}
                        onSelect={flow.setCurrentSectionId}
                        missingBySection={flow.missingBySection}
                      />
                      <ScrollBar orientation="vertical" className="w-2.5 bg-slate-200/80" />
                    </ScrollArea>
                  </div>

                  <div className="min-h-0 h-full overflow-hidden rounded-2xl">
                    <ScrollArea
                      type="always"
                      className="min-h-0 h-full"
                      data-testid={`${dataTestIdPrefix}-panel-wrapper`}
                    >
                      <div className="pr-2">
                        <CustomizationGuidedPanel
                          section={flow.currentSection}
                          values={values}
                          onValueChange={(optionId, nextValue) => setValues?.((prev) => ({ ...prev, [optionId]: nextValue }))}
                          formatCurrency={formatCurrency}
                          missingRequired={flow.currentMissingRequired}
                          showDelete={showDelete}
                          onDeleteField={onDeleteField}
                          onAddCustomField={onAddCustomField}
                          showAddCustomField={showAddCustomField}
                        />
                      </div>
                      <ScrollBar orientation="vertical" className="w-2.5 bg-slate-200/80" />
                    </ScrollArea>
                  </div>

                  <div
                    className="hidden lg:flex lg:min-h-0 lg:h-full lg:flex-col rounded-2xl border border-slate-200 bg-slate-50 p-3"
                    data-testid={`${dataTestIdPrefix}-reference-panel`}
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Shared Order Details
                    </p>
                    {referencePanel?.sourceProductName && (
                      <p className="mt-0.5 text-[11px] text-slate-400" data-testid={`${dataTestIdPrefix}-reference-source`}>
                        From: {referencePanel.sourceProductName}
                      </p>
                    )}
                    <div className="mt-2 space-y-2 overflow-y-auto">
                      {(!referencePanel?.items || referencePanel.items.length === 0) ? (
                        <p className="text-xs italic text-slate-400" data-testid={`${dataTestIdPrefix}-reference-empty`}>
                          No previous product in this order yet. Fabric and button details from the next product you customize will appear here for quick copying.
                        </p>
                      ) : (
                        referencePanel.items.map((item) => (
                          <div
                            key={item.key}
                            className="rounded-lg border border-slate-200 bg-white p-2"
                            data-testid={`${dataTestIdPrefix}-reference-item-${item.key}`}
                          >
                            <p className="text-[10px] uppercase text-slate-400">{item.label}</p>
                            <div className="mt-0.5 flex items-center justify-between gap-1">
                              <span className="truncate text-sm font-medium text-slate-800" title={item.value}>
                                {item.value}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleCopyReferenceValue(item)}
                                className="shrink-0 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                data-testid={`${dataTestIdPrefix}-reference-copy-${item.key}`}
                                title={`Copy ${item.label}`}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="shrink-0 bg-white px-4 pb-4 md:px-6" data-testid={`${dataTestIdPrefix}-footer-shell`}>
          <CustomizationFooterBar
            hasPrevious={flow.hasPrevious}
            hasNext={flow.hasNext}
            canGoNext={flow.canGoNext}
            canSubmit={flow.canSubmit}
            missingCount={flow.allMissingRequired.length}
            onPrevious={flow.goPrevious}
            onNext={flow.goNext}
            onCancel={() => handleClose(false)}
            onSubmit={handleSubmit}
            submitLabel={submitLabel}
            isSubmitting={isSubmitting}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
